// polyfills
import 'react-app-polyfill/ie11'
import 'babel-polyfill'
import 'raf/polyfill'

// on to the fun
import React from 'react'
import ReactDOM from 'react-dom'
import '@foodsby/nutrient/dist/nutrient.css'
import './app.css'
import { Provider } from 'react-redux'
import { Switch } from 'react-router-dom'
import { ConnectedRouter } from 'connected-react-router'
import store, { history } from './redux/store'
import loadGoogleMapsApi from 'load-google-maps-api'
import {
  changePasswordRoute,
  checkoutInterstitialRoute,
  checkoutRoute,
  completeAccountRoute,
  confirmationRoute,
  contactRoute,
  contactUsRoute,
  creditsRoute,
  forgotPasswordRoute,
  friendsRoute,
  historyRoute,
  homeRoute,
  joinRoute,
  locationRoute,
  loginRoute,
  logoutRoute,
  maintenanceRoute,
  noLocationRoute,
  personalizeRoute,
  placeOrderRoute,
  privacyPolicyRoute,
  profileRoute,
  registerRoute,
  rootRoute,
  selectLocationRoute,
  termsAndConditionsRoute,
  thankYouRoute,
  unsubscribeRoute
} from './routes/routes'
import {
  SidebarLayoutRoute,
  FooterLayoutRoute,
  PublicLayoutRoute,
  SimpleLayoutRoute
} from './routes/layoutRoutes'
import { Home, LocationSidebar } from './components/home'
import { initTokens } from './util/webStorageUtils'
import { PlaceOrder, OrderNav, OrderSidebar } from './components/place-order'
import { Route } from 'react-router'
import ChangePassword from './components/changePassword/ChangePassword'
import Checkout from './components/checkout/Checkout'
import CheckoutInterstitial from './components/checkout/CheckoutInterstitial'
import CheckoutSidebar from './components/checkout/CheckoutSidebar'
import CompleteAccount from './components/complete-account/CompleteAccount'
import Confirmation from './components/confirmation/Confirmation'
import Contact from './components/contact-us/Contact'
import ContactUs from './components/contact-us/ContactUs'
import CreditsPage from './components/credit/CreditsPage'
import DefaultNav from './components/common/DefaultNav'
import ForgotPassword from './components/login/ForgotPassword'
import FriendManager from './components/community/FriendManager'
import FriendRequestSidebar from './components/community/FriendRequestSidebar'
import FriendsSidebar from './components/community/FriendsSidebar'
import HomeRedirect from './components/home/HomeRedirect'
import JoinPage from './components/join/JoinPage'
import LoginPage from './components/login/LoginPage'
import Logout from './components/login/Logout'
import Maintenance from './components/maintenance/Maintenance'
import NoLocationPage from './components/location-search/NoLocationPage'
import NotFound from './components/not-found/NotFound'
import OrderHistory from './components/history/OrderHistory'
import PersonalizeOrder from './components/personalize-order/PersonalizeOrder'
import PrivacyPolicyPage from './components/legal/PrivacyPolicyPage'
import Profile from './components/profile/Profile'
import ProfileSidebar from './components/profile/ProfileSidebar'
import Register from './components/register/Register'
import SelectLocationPage from './components/location-search/SelectLocationPage'
import Sticky from './components/common/Sticky'
import TermsOfUsePage from './components/legal/TermsOfUsePage'
import ThankYou from './components/contact-us/ThankYou'
import UnsubscribePage from './components/unsubscribe/UnsubscribePage'
import LogRocket from 'logrocket'
import setupLogRocketReact from 'logrocket-react'
import { logException } from './util/errorUtils'

// set up JWT
initTokens()
function inIframe() {
  try {
    return window.self !== window.top
  } catch (e) {
    return true
  }
}

async function renderApp() {
  try {
    // set up logrocket
    if (process.env.REACT_APP_LOGROCKET_ID) {
      LogRocket.init(process.env.REACT_APP_LOGROCKET_ID, {
        network: {
          requestSanitizer: function(req) {
            if (req.headers['Authorization']) {
              // remove token
              req.headers['Authorization'] = 'REDACTED'
            }
            if (req.url.indexOf('heartlandportico') > -1) {
              // remove heartland
              return null
            }
            if (req.url.indexOf('/token') > -1) {
              req.body = 'REDACTED'
            }

            return req
          },
          responseSanitizer: function(res) {
            if (res.url.indexOf('/oauth/token') > -1) {
              // Remove token from user res
              res.body = 'REDACTED'
            }
            return res
          }
        },
        console: {
          shouldAggregateConsoleErrors: true
        }
      })
      setupLogRocketReact(LogRocket)
    }

    // set up gmaps
    await loadGoogleMapsApi({
      key: 'AIzaSyBDH0Ysz12SLHmVyPo0nsisYMBY6kaUAcU',
      libraries: ['places']
    })

    // mount app
    const root = document.getElementById('root')
    if (root) {
      ReactDOM.render(
        <Provider store={store}>
          <ConnectedRouter history={history}>
            <Sticky>
              <Switch>
                <PublicLayoutRoute
                  path={loginRoute.path}
                  title={loginRoute.title}
                  component={LoginPage}
                />
                <PublicLayoutRoute
                  path={joinRoute.path}
                  title={joinRoute.title}
                  component={JoinPage}
                />
                <PublicLayoutRoute
                  path={registerRoute.path}
                  title={registerRoute.title}
                  component={Register}
                />
                <FooterLayoutRoute
                  path={contactUsRoute.path}
                  title={contactUsRoute.title}
                  component={ContactUs}
                />
                <FooterLayoutRoute
                  path={thankYouRoute.path}
                  title={thankYouRoute.title}
                  component={ThankYou}
                />
                <FooterLayoutRoute
                  path={privacyPolicyRoute.path}
                  title={privacyPolicyRoute.title}
                  component={PrivacyPolicyPage}
                />
                <FooterLayoutRoute
                  path={termsAndConditionsRoute.path}
                  title={termsAndConditionsRoute.title}
                  component={TermsOfUsePage}
                />
                <PublicLayoutRoute
                  path={changePasswordRoute.path}
                  title={changePasswordRoute.title}
                  component={ChangePassword}
                />
                <PublicLayoutRoute
                  path={forgotPasswordRoute.path}
                  title={forgotPasswordRoute.title}
                  component={ForgotPassword}
                />
                <SimpleLayoutRoute
                  path={completeAccountRoute.path}
                  title={completeAccountRoute.title}
                  component={CompleteAccount}
                />
                <PublicLayoutRoute
                  path={selectLocationRoute.path}
                  title={selectLocationRoute.title}
                  component={SelectLocationPage}
                />
                <Route
                  exact
                  path={homeRoute.path}
                  title={homeRoute.title}
                  component={HomeRedirect}
                />
                <Route
                  exact
                  path={rootRoute.path}
                  title={rootRoute.title}
                  component={HomeRedirect}
                />
                <SidebarLayoutRoute
                  exact
                  path={locationRoute.path}
                  title={locationRoute.title}
                  component={Home}
                  sidebarRight={[FriendsSidebar, LocationSidebar]}
                  overrideAuthRedirect
                />
                <SidebarLayoutRoute
                  exact
                  path={placeOrderRoute.path}
                  title={placeOrderRoute.title}
                  component={PlaceOrder}
                  nav={OrderNav}
                  sidebarRight={OrderSidebar}
                />
                <SidebarLayoutRoute
                  path={personalizeRoute.path}
                  title={personalizeRoute.title}
                  component={PersonalizeOrder}
                  nav={OrderNav}
                  sidebarRight={OrderSidebar}
                  noScroll="true"
                />
                <SidebarLayoutRoute
                  path={confirmationRoute.path}
                  title={confirmationRoute.title}
                  component={Confirmation}
                  nav={props => (
                    <DefaultNav pageName="Confirm Order" {...props} />
                  )}
                />
                <SidebarLayoutRoute
                  path={historyRoute.path}
                  title={historyRoute.title}
                  component={OrderHistory}
                  nav={props => (
                    <DefaultNav pageName="Order History" {...props} />
                  )}
                />
                <SidebarLayoutRoute
                  path={checkoutInterstitialRoute.path}
                  title={checkoutInterstitialRoute.title}
                  component={CheckoutInterstitial}
                  nav={props => <DefaultNav pageName="Checkout" {...props} />}
                  sidebarRight={OrderSidebar}
                />
                <SidebarLayoutRoute
                  path={checkoutRoute.path}
                  title={checkoutRoute.title}
                  component={Checkout}
                  nav={props => <DefaultNav pageName="Checkout" {...props} />}
                  sidebarRight={CheckoutSidebar}
                />
                <SidebarLayoutRoute
                  path={creditsRoute.path}
                  title={creditsRoute.title}
                  component={CreditsPage}
                  nav={props => <DefaultNav pageName="Credits" {...props} />}
                />
                <SidebarLayoutRoute
                  path={profileRoute.path}
                  title={profileRoute.title}
                  component={Profile}
                  nav={props => (
                    <DefaultNav pageName="User Profile" {...props} />
                  )}
                  sidebarRight={[ProfileSidebar]}
                />
                <SidebarLayoutRoute
                  path={contactRoute.path}
                  title={contactRoute.title}
                  component={Contact}
                  nav={props => <DefaultNav pageName="Contact Us" {...props} />}
                />
                <SidebarLayoutRoute
                  path={friendsRoute.path}
                  title={friendsRoute.title}
                  component={FriendManager}
                  nav={props => <DefaultNav pageName="Friends" {...props} />}
                  sidebarRight={FriendRequestSidebar}
                />
                <PublicLayoutRoute
                  path={noLocationRoute.path}
                  title={noLocationRoute.title}
                  component={NoLocationPage}
                />
                <Logout path={logoutRoute.path} title={logoutRoute.title} />
                <PublicLayoutRoute
                  path={maintenanceRoute.path}
                  title={maintenanceRoute.title}
                  component={Maintenance}
                />
                <PublicLayoutRoute
                  path={unsubscribeRoute.path}
                  title={unsubscribeRoute.title}
                  component={UnsubscribePage}
                />
                {/* This one goes last */}
                <PublicLayoutRoute title="Not found" component={NotFound} />
              </Switch>
            </Sticky>
          </ConnectedRouter>
        </Provider>,
        root
      )
    }
  } catch (ex) {
    logException(ex)
  }
}

// REDIRECT OLD URLS HERE
if (window.location.pathname.indexOf('/home/') > -1) {
  window.location = window.location.pathname.replace('/home', '')
} else if (!inIframe()) {
  renderApp()
}
